import RoomData from "../interfaces/robot/rooms/room-data";
import { FloorStation } from "../interfaces/robot/rooms/station";


export const listedRoomCardWidthPx = 240;

const deg2rad = (deg: number) => (deg * Math.PI) / 180;

/** Convert CW degrees to math radians (CCW, y-up) */
const toMathRad = (cwDeg: number) => -deg2rad(cwDeg);

/** Normalize CW degrees to [0, 360) */
const normCW = (degCW: number) => {
  let d = degCW % 360;
  if (d < 0) d += 360;
  return d;
};

export const computeAbsolutePositions = (
  rooms: RoomData[],
): { stations: FloorStation[]; coordinateX: number; coordinateY: number } => {
  const graph: Record<string, { to: string; dX: number; dY: number; dTheta: number }[]> = {};
  if (rooms.length === 0) return { stations: [], coordinateX: 0, coordinateY: 0 };

  for (const { primaryStation, secondaryStation } of rooms) {
    if (secondaryStation == null) continue;

    if (!graph[primaryStation.stationId]) graph[primaryStation.stationId] = [];
    if (!graph[secondaryStation.stationId]) graph[secondaryStation.stationId] = [];

    // a -> b transform
    graph[primaryStation.stationId].push({
      to: secondaryStation.stationId,
      dX: secondaryStation.coordinateX,
      dY: secondaryStation.coordinateY,
      dTheta: secondaryStation.coordinateTheta,
    });

    // b -> a is inverse transform
    const dxm = secondaryStation.coordinateX;
    const dym = -secondaryStation.coordinateY;
    const th_m = toMathRad(secondaryStation.coordinateTheta);
    const c = Math.cos(th_m);
    const s = Math.sin(th_m);

    // Inverse in math: t_inv_m = -R(θ_m) * t_m
    const invDxM = -(c * dxm - s * dym);
    const invDyM = -(s * dxm + c * dym);

    // Back to screen: [dx, dy] = [inv_dx_m, -inv_dy_m], θ_cw = -θ_cw
    const invDxS = invDxM;
    const invDyS = -invDyM;
    const invDThetaCW = -secondaryStation.coordinateTheta;

    graph[secondaryStation.stationId].push({
      to: primaryStation.stationId,
      dX: invDxS,
      dY: invDyS,
      dTheta: invDThetaCW,
    });
  }

  const absoluteStations: FloorStation[] = [];
  const queue: string[] = [];

  const startId = rooms[0].primaryStation.stationId;
  absoluteStations.push({ id: startId, coordinateX: 0, coordinateY: 0, coordinateTheta: 0, isTransition: false });
  queue.push(startId);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const {
      coordinateX: Xp,
      coordinateY: Yp,
      coordinateTheta: TpCW,
    } = absoluteStations.find((fS) => fS.id === current)!;

    // Convert parent heading to math radians
    const Tp_m = toMathRad(TpCW);

    for (const edge of graph[current] || []) {
      if (!absoluteStations.some((fS) => fS.id === edge.to)) {
        const Xc = Xp + Math.cos(Tp_m) * edge.dX - Math.sin(Tp_m) * edge.dY;
        const Yc = Yp - (Math.sin(Tp_m) * edge.dX + Math.cos(Tp_m) * edge.dY);

        const TcCW = normCW(TpCW + edge.dTheta);

        absoluteStations.push({
          id: edge.to,
          coordinateX: Xc,
          coordinateY: Yc,
          coordinateTheta: TcCW,
          isTransition: false,
        });
        queue.push(edge.to);
      }
    }
  }

  rooms.forEach((r) => {
    const primary = absoluteStations.find((station) => station.id === r.primaryStation.stationId);
    const secondary = absoluteStations.find((station) => station.id === r.secondaryStation?.stationId);

    if (primary && secondary) {
      const midpointX = (primary.coordinateX + secondary.coordinateX) / 2;
      const midpointY = (primary.coordinateY + secondary.coordinateY) / 2;
      const midpointTheta = primary.coordinateTheta;

      const midpointId = `${primary.id},${secondary.id}`;

      // Check if midpoint already exists to prevent duplicates
      if (!absoluteStations.some((station) => station.id === midpointId)) {
        absoluteStations.push({
          id: midpointId,
          coordinateX: midpointX,
          coordinateY: midpointY,
          coordinateTheta: midpointTheta,
          isTransition: true,
        });
      }
    }
  });

  // Now, compute the minimum distance between any two stations
  let minDistance = Infinity;

  for (let i = 0; i < absoluteStations.length; i++) {
    for (let j = i + 1; j < absoluteStations.length; j++) {
      const dx = absoluteStations[i].coordinateX - absoluteStations[j].coordinateX;
      const dy = absoluteStations[i].coordinateY - absoluteStations[j].coordinateY;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < minDistance && distance > 0) {
        minDistance = distance;
      }
    }
  }

  if (minDistance === Infinity || minDistance === 0) {
    return { stations: absoluteStations, coordinateX: 0, coordinateY: 0 };
  }

  const MIN_SEP = 340;

  // Non-linear: fit everything into a viewport sized ~sqrt(N) * MIN_SEP per axis,
  // then push apart only the pairs that end up too close.
  // This prevents one tight pair from linearly blowing up the entire layout.
  const xVals = absoluteStations.map((s) => s.coordinateX);
  const yVals = absoluteStations.map((s) => s.coordinateY);
  const rawRange = Math.max(Math.max(...xVals) - Math.min(...xVals), Math.max(...yVals) - Math.min(...yVals));
  const scaleFactor = rawRange > 0 ? (MIN_SEP * Math.sqrt(absoluteStations.length)) / rawRange : 1;

  absoluteStations.forEach((station) => {
    station.coordinateX *= scaleFactor;
    station.coordinateY *= scaleFactor;
  });

  // Repulsion pass: push any real-station pairs closer than MIN_SEP apart.
  for (let iter = 0; iter < 200; iter++) {
    let moved = false;
    for (let i = 0; i < absoluteStations.length; i++) {
      for (let j = i + 1; j < absoluteStations.length; j++) {
        const a = absoluteStations[i];
        const b = absoluteStations[j];
        const dx = b.coordinateX - a.coordinateX;
        const dy = b.coordinateY - a.coordinateY;
        const d = Math.sqrt(dx * dx + dy * dy) || 0.001;
        if (d < MIN_SEP) {
          moved = true;
          const half = (MIN_SEP - d) * 0.5;
          const nx = dx / d;
          const ny = dy / d;
          a.coordinateX -= nx * half;
          a.coordinateY -= ny * half;
          b.coordinateX += nx * half;
          b.coordinateY += ny * half;
        }
      }
    }
    if (!moved) break;
  }

  // Find the minimum scaled X and Y to shift coordinates
  let minScaledX = Infinity;
  let minScaledY = Infinity;

  absoluteStations.forEach((station) => {
    if (station.coordinateX! < minScaledX) {
      minScaledX = station.coordinateX!;
    }
    if (station.coordinateY! < minScaledY) {
      minScaledY = station.coordinateY!;
    }
  });

  // Ensure minScaledX and minScaledY are finite numbers
  if (minScaledX === Infinity || minScaledY === Infinity) {
    return { stations: absoluteStations, coordinateX: 0, coordinateY: 0 };
  }

  let maxCoordinateX = 0;
  let maxCoordinateY = 0;
  // Shift all scaled stations so that the leftmost X is 0 and topmost Y is 0
  absoluteStations.forEach((station) => {
    station.coordinateX = station.coordinateX! - minScaledX;
    station.coordinateY = station.coordinateY! - minScaledY;
    if (maxCoordinateX < station.coordinateX) {
      maxCoordinateX = station.coordinateX;
    }
    if (maxCoordinateY < station.coordinateY) {
      maxCoordinateY = station.coordinateY;
    }
  });
  return {
    stations: absoluteStations,
    coordinateX: maxCoordinateX,
    coordinateY: maxCoordinateY,
  };
};
